import Head from 'next/head'
import { PersonalTrainer } from '../../../components/index/service-category/personal-trainer/PersonalTrainer'
import { getRequestJSON } from '../../../api/index/location-api';
import Script from 'next/script'
import { contentDate } from '../../../utils/article-date-time';
import { countryList } from 'utils/constants';
import { getTestimonials } from 'api/user/profile-api';
import { getPtTestimonials, setPtTestimonials } from 'service/testimonial-store';
import { downloadServiceProviders, downloadTopServiceProviders } from 'api/service-provider/sp-list';
import { getDistanceMetrics } from 'utils/distance-matrix';
import { toMapMarkers, toTrainerCards } from 'utils/trainer-card-projection';
import { withDevRosterFallback } from 'utils/dev-roster-fallback';
import { buildPersonalTrainerSchema } from 'utils/pt-page-schema';

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-10-11'

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export async function getStaticProps() {

    const { modifiedDate, month, year } = contentDate(CONTENT_REVIEWED);

    const countryName = "Singapore"
    
    const allRequestCategorySettings = await getRequestJSON(countryName)

    const testimonials = getPtTestimonials().length > 0 ? getPtTestimonials() : await getTestimonials("Fitness Trainer", 0, 6)
    setPtTestimonials(testimonials)

    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Fitness Trainer")
    
    const distanceUnit = getDistanceMetrics(countryName)
    const lat = 1.3521
    const lng = 103.8198

    // 18, not 12: the hero strip takes the first HERO_TRAINER_COUNT (6) and the
    // Meet Our Trainers grid takes the rest, so 18 keeps the grid at its original
    // 12 cards while adding 6 distinct trainers to the page rather than repeating them.
    const spListRaw =  await downloadTopServiceProviders("Fitness Trainer", 0, 18, countryName, lat, lng, 50, distanceUnit, false, "")
    // NOTE: capped at 500 records. If the trainer roster ever exceeds 500 this count will silently cap.
    const spListMapRaw =  await downloadServiceProviders("Fitness Trainer", 0, 500, "Singapore", lat, lng, 50, "kms", false, false, true)

    // The dev API serves one placeholder provider, which leaves the trainer strip,
    // trainer grid and map empty and makes this page undevelopable locally. These
    // are pass-throughs in a production build - see utils/dev-roster-fallback.
    const spList = withDevRosterFallback(spListRaw, "top trainers")
    const spListMap = withDevRosterFallback(spListMapRaw, "trainer map")

    // Trainer count used in the page title, meta tags and hero copy.
    // Falls back to a safe baseline if the roster fetch returns empty/short at build time,
    // otherwise an API hiccup would ship "0 Certified Trainers" for the full revalidate window.
    const rawTrainerCount = Array.isArray(spListMap) ? spListMap.length : 0
    const trainerCount = rawTrainerCount >= 50 ? rawTrainerCount : 188
    // const fitnessBlogs = blogsMaster['health-fitness'].blogArray;
    // const wpPosts = await getWpPostsByCategory(6, "health-fitness");
    // const wpNewPosts = await getWpNewPostsByCategory(6, "health-fitness");

    let spLocationList = []

    for(let x = 0; x < spListMap.length; x++){
        let listA = [spListMap[x].userFirstName, spListMap[x].userLatitude, spListMap[x].userLongitude, x+1]
        spLocationList.push(listA)
    }

    // Trainer cards show a 2-line bio only — trim the payload. See utils/trainer-card-projection.
    const spCards = toTrainerCards(spList)

    // Only these fields are read by the map info-window (see PersonalTrainer.js initMap).
    // Serialising the full provider records into __NEXT_DATA__ was the bulk of the page payload,
    // so project down to the minimum before shipping to the client. toMapMarkers also
    // normalises userLocation, which otherwise ships trainers' home addresses.
    const spMapMarkers = toMapMarkers(spListMap)

    const getLocalityList = () => {
      for(let x in countryList){
          if(countryList[x].country_name === countryName){
              return countryList[x].localityList
          }
      }
    } 
  
    const localityList = getLocalityList()

  return {
      props: {
        requestCategorySettings,
        countryName,
        modifiedDate,
        month,
        year,    
        localityList,
        testimonials,
        spList: spCards,
        spListMap: spMapMarkers,
        spLocationList,
        trainerCount
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
// export async function getStaticPaths() {

//     // Get the paths we want to pre-render based on posts
//     const paths = countryList.map((country) => ({
//       params: { countryCd: country.country_cd },
//     }))

//     // We'll pre-render only these paths at build time.
//     // { fallback: blocking } will server-render pages
//     // on-demand if the path doesn't exist.
//     return { paths, fallback: 'blocking' }
// }

export default function Home(props) {
    const requestCategorySettings = props.requestCategorySettings
    const modifiedDate = props.modifiedDate
    // const fitnessBlogs = props.fitnessBlogs
    // const wpPosts = props.wpPosts
    // const wpNewPosts = props.wpNewPosts
    const month = props.month
    const year = props.year
    const localityList = props.localityList
    const testimonials = props.testimonials
    const spList = props.spList
    const spListMap = props.spListMap
    const spLocationList = props.spLocationList
    const trainerCount = props.trainerCount

    const pageTitle = `Personal Trainer Singapore | ${trainerCount} Certified Trainers, Home or Gym`
        
  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta httpEquiv="content-language" content="en-sg" />
        <meta name="description"
            content="Book a certified personal trainer in Singapore from $84/session. Train at home, your condo gym or outdoors. Free consultation, transparent pricing, 5,000+ clients since 2018. No gym membership needed."/>
        <meta name="author" content="StringsSG"/>
        <meta name="geo.region" content="SG" />
        <meta name="geo.placename" content="Singapore" />
        <meta name="geo.position" content="1.3521;103.8198" />
        <meta name="ICBM" content="1.3521, 103.8198" />
        <meta name="google-site-verification"
            content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_SG" />
        <meta property="og:locale:alternate" content="en_US" />
        <meta property="og:site_name" content="StringsSG" />
        <meta property="og:url" content={`https://www.stringssg.com/sg/personal-trainer`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle}/>
        <meta property="og:description" content="Book a certified personal trainer in Singapore from $84/session. Train at home, your condo gym or outdoors. Free consultation, transparent pricing, 5,000+ clients since 2018. No gym membership needed."/>
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2022-12-07T09:00:00+08:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg" />
        <meta property="og:image:secure_url" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Personal trainer training a client at home gym in Singapore" />
        <meta property="og:video" content="https://www.youtube.com/watch?v=CC4Pe6-I1PY"/>
        <meta property="og:video:secure_url" content="https://www.youtube.com/watch?v=CC4Pe6-I1PY"/>
        <meta property="og:video:type" content="text/html" />
        <meta property="og:video:width" content="840"/>
        <meta property="og:video:height" content="473"/>
        <meta property="video:duration" content="16"/>
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:creator" content="@stringssg"/>
        <meta name="twitter:url" content={`https://www.stringssg.com/sg/personal-trainer`}/>
        <meta name="twitter:title" content={pageTitle}/>
        <meta name="twitter:description" content="Book a certified personal trainer in Singapore from $84/session. Train at home, your condo gym or outdoors. Free consultation, transparent pricing, 5,000+ clients since 2018."/>
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg" />
        <meta name="twitter:image:alt" content="Personal trainer training a client at home gym in Singapore" />
        <link rel="icon" href="/favicon.ico" />
        <script type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(buildPersonalTrainerSchema({ pageTitle, modifiedDate })) }}
        />
        <link rel="canonical" href="https://www.stringssg.com/sg/personal-trainer" />
        <link rel="alternate" hrefLang="en-sg" href="https://www.stringssg.com/sg/personal-trainer" />
        <link rel="alternate" hrefLang="en" href="https://www.stringssg.com/sg/personal-trainer" />
        {/* Regional variants - must match PT_HUB_ALTERNATES in components/market/PtTopicPage. */}
        <link rel="alternate" hrefLang="en-ae" href="https://www.stringssg.com/ae/personal-trainer-dubai" />
        <link rel="alternate" hrefLang="en-my" href="https://www.stringssg.com/my/personal-trainer-kuala-lumpur" />
        <link rel="alternate" hrefLang="ms-my" href="https://www.stringssg.com/my/personal-trainer-kuala-lumpur/jurulatih-peribadi-kl" />
        <link rel="alternate" hrefLang="x-default" href="https://www.stringssg.com/sg/personal-trainer" />
      </Head> 
        <div className="">
            <PersonalTrainer requestCategorySettings={requestCategorySettings} year={year} localityList={localityList} testimonials={testimonials}
              spList={spList} spListMap={spListMap} spLocationList={spLocationList} trainerCount={trainerCount}
            />           
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '128634041116395');
                fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
